import {
  type FormControlProps,
  type SelectProps,
  FilledInput,
  FormControl,
  InputLabel,
  OutlinedInput,
  Select,
  Typography,
} from '@material-ui/core';
import { makeStyles, styled, withStyles } from '@material-ui/core/styles';
import { Colors } from '@rentguru/commons-utils';
import { CSSProperties, SVGProps } from 'react';

const MyFilledInput = withStyles({
  root: {
    '&$disabled:before': {
      borderBottomStyle: 'hidden',
    },
  },
  disabled: {
    borderRadius: 6,
    backgroundColor: Colors.PORCELAIN_GREY_1,
  },
  underline: {
    '&:before': {
      borderBottom: `2px solid ${Colors.TOWER_GREY}`,
    },
  },
})(FilledInput);

const MyInputLabel = styled(InputLabel)({
  color: Colors.LIGHT_BLUE_GREY,
});

const MySelect = withStyles({
  icon: {
    fill: Colors.BLUEY,
  },
})(Select);

const useStyles = makeStyles({
  cardFieldLabel: {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: 1,
    textTransform: 'uppercase',
    color: Colors.DARK_SLATE_GREY,
    marginBottom: 2,
  },
  outlinedRoot: {
    borderRadius: 8,
    minHeight: 46,
    alignItems: 'center',
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: `${Colors.LIGHT_BLUE_GREY} !important`,
    },
  },
  outlinedSelect: {
    '&:focus': {
      backgroundColor: 'transparent',
    },
    display: 'flex',
    alignItems: 'center',
  },
  selectIcon: {
    fill: Colors.BLUEY,
  },
});

export interface CustomizedSelectProps {
  error?: boolean;
  label?: string;
  fieldName?: string;
  value: unknown;
  onChange?: (value: string) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  handleChange?: (event: React.ChangeEvent<any>) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onBlur?: (e: any) => void;
  FormControlProps?: Partial<FormControlProps>;
  SelectProps?: Partial<SelectProps>;
  disabled?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  className?: any;
  multiple?: boolean;
  margin?: 'normal' | 'dense';
  id?: string;
  dataTest?: string;
  inputLabelShrink?: boolean;
  style?: CSSProperties;
  children?: React.ReactNode;
  IconComponent?: React.ElementType<SVGProps<SVGSVGElement>, keyof React.JSX.IntrinsicElements>;
  modern?: boolean;
}

export const CustomizedSelect = ({
  children,
  error,
  label,
  fieldName,
  onChange,
  handleChange,
  onBlur,
  FormControlProps = {},
  SelectProps = {},
  value,
  disabled = false,
  className,
  multiple = false,
  margin = 'dense',
  id,
  inputLabelShrink,
  style,
  dataTest = 'customizedSelect',
  IconComponent,
  modern = false,
}: CustomizedSelectProps) => {
  const classes = useStyles();

  const handleSelectChange = onChange
    ? (e: React.ChangeEvent<{ value: unknown }>) => {
        onChange(e.target.value as string);
      }
    : handleChange;

  if (modern) {
    return (
      <div style={{ width: '100%' }}>
        <Typography className={classes.cardFieldLabel}>{label}</Typography>
        <FormControl
          id={id}
          variant="outlined"
          margin={margin}
          component="div"
          error={error}
          disabled={disabled}
          className={className}
          data-test={dataTest}
          {...(onBlur && { onBlur })}
          {...FormControlProps}
        >
          <MySelect
            value={value}
            onChange={handleSelectChange}
            classes={{ icon: classes.selectIcon, select: classes.outlinedSelect }}
            input={
              <OutlinedInput name={fieldName} id={fieldName} labelWidth={0} classes={{ root: classes.outlinedRoot }} />
            }
            {...SelectProps}
            IconComponent={IconComponent}
            multiple={multiple}
            style={style}
          >
            {children}
          </MySelect>
        </FormControl>
      </div>
    );
  }

  return (
    <FormControl
      id={id}
      variant="filled"
      margin={margin}
      component="div"
      error={error}
      disabled={disabled}
      className={className}
      data-test={dataTest}
      {...(onBlur && { onBlur })}
      {...FormControlProps}
    >
      <MyInputLabel {...(inputLabelShrink ? { shrink: true } : {})} htmlFor={fieldName}>
        {label}
      </MyInputLabel>
      <MySelect
        value={value}
        onChange={handleSelectChange}
        input={<MyFilledInput name={fieldName} id={fieldName} />}
        {...SelectProps}
        IconComponent={IconComponent}
        multiple={multiple}
        style={style}
      >
        {children}
      </MySelect>
    </FormControl>
  );
};
